import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  StudySetup,
  ExternalDataSourceType,
  EDC_SYSTEMS,
  FUNDING_AGENCIES,
  REPORTING_REQUIREMENTS,
  EXTERNAL_DATA_SOURCE_OPTIONS,
  NOT_DECIDED_LABEL,
  COUNTRY_OPTIONS,
  BLINDING_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  ProductTypeSetting,
  OVERSIGHT_KIND_OPTIONS,
  PARTY_ROLE_OPTIONS,
  SAE_ROUTE_OPTIONS,
  LAB_MODEL_OPTIONS,
  IMAGING_MODEL_OPTIONS,
  DURATION_UNIT_OPTIONS,
  DICTIONARY_FIELDS,
  BlindingSetting,
  OversightSetting,
  OversightKindSetting,
  PartyRole,
  SafetyReportingSetting,
  SaeRouteSetting,
  TimelineSettings,
  DurationSetting,
  DurationUnit,
  StudyPartSetting,
} from '../constants/studySetupOptions';
import {
  otherCountriesAfterBlur,
  unmatchedCountriesText,
  unrecognisedCodesText,
  unrecognisedCountryCodes,
  unrecognisedNamesText,
} from '../utils/countriesFieldRule';

import {
  DEFAULT_STUDY_DATE_FORMAT,
  STUDY_DATE_FORMATS,
  type StudyDateFormat,
} from '../../../functions/src/studyProfile/studyDateFormat';

/** The default is the empty value, so a study that has chosen nothing shows it as selected. */
const DATE_FORMAT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = STUDY_DATE_FORMATS.map((f) => ({
  value: f.value === DEFAULT_STUDY_DATE_FORMAT ? '' : f.value,
  label: `${f.value}  (${f.example})${f.value === DEFAULT_STUDY_DATE_FORMAT ? ' - default' : ''}`,
}));

interface StudySetupFormProps {
  value: Partial<StudySetup>;
  onChange: (setup: Partial<StudySetup>) => void;
  highlightFields?: string[];
  compact?: boolean;
}

type YesNoUnknown = 'yes' | 'no' | 'unknown';

const inputClass =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-300 transition-all';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

function highlightWrap(fieldName: string, highlightFields?: string[]): string {
  if (highlightFields?.includes(fieldName)) {
    return 'ring-2 ring-amber-300 bg-amber-50 transition-all rounded-lg p-1 -m-1';
  }
  return '';
}

function SectionHeader({
  title,
  expanded,
  onToggle,
  note,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  /** A short reminder shown next to the title, e.g. a value waiting to be confirmed. */
  note?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-1.5 w-full text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-2"
    >
      {expanded ? (
        <ChevronDown className="w-3.5 h-3.5" />
      ) : (
        <ChevronRight className="w-3.5 h-3.5" />
      )}
      {title}
      {note && <span className="ml-2 normal-case tracking-normal font-medium text-amber-700">{note}</span>}
    </button>
  );
}

function RadioGroup({
  name,
  value,
  onChange,
}: {
  name: string;
  value: YesNoUnknown | undefined;
  onChange: (v: YesNoUnknown) => void;
}) {
  const options: { value: YesNoUnknown; label: string }[] = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: 'Unknown' },
  ];

  return (
    <div className="flex items-center gap-4">
      {options.map((opt) => (
        <label key={opt.value} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="accent-primary-600"
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

/** A labelled select. `value` absent shows the first option ("not yet decided"). */
function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  help,
}: {
  label: string;
  value: T | undefined;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (v: T) => void;
  help?: string;
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <select
        value={value ?? options[0]?.value}
        onChange={(e) => onChange(e.target.value as T)}
        className={inputClass}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {help && <p className="text-[11px] text-gray-400 mt-1">{help}</p>}
    </div>
  );
}

/** A labelled text input that stores `undefined` when emptied. */
function TextField({
  label,
  value,
  onChange,
  placeholder,
  help,
}: {
  label: string;
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  placeholder?: string;
  help?: string;
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <input
        type="text"
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        className={inputClass}
      />
      {help && <p className="text-[11px] text-gray-400 mt-1">{help}</p>}
    </div>
  );
}

/** A number plus unit. Clearing the number clears the timeline (it becomes TBD). */
function DurationField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: DurationSetting | undefined;
  onChange: (v: DurationSetting | undefined) => void;
}) {
  const unit: DurationUnit = value?.unit ?? 'business_days';
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex gap-2">
        <input
          type="number"
          min={1}
          placeholder="Not yet decided"
          value={value?.value ?? ''}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange(e.target.value === '' || !(n > 0) ? undefined : { value: n, unit });
          }}
          className={`${inputClass} max-w-[8rem]`}
        />
        <select
          value={unit}
          onChange={(e) => value && onChange({ value: value.value, unit: e.target.value as DurationUnit })}
          className={inputClass}
        >
          {DURATION_UNIT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

/** Listed countries as checkboxes, other two-letter codes as text (applied when the box loses focus). */
function CountriesField({
  value,
  onChange,
  fromProtocol,
  unmapped,
  onConfirm,
  leftover,
  onLeftover,
}: {
  value: string[] | undefined;
  onChange: (v: string[] | undefined) => void;
  /** The list was filled in from the protocol and is not yet confirmed. */
  fromProtocol?: boolean;
  /** Countries the protocol names that could not be matched to a code. */
  unmapped?: string[];
  onConfirm?: () => void;
  /**
   * Typed entries not recognised as a country, kept by the parent so they
   * survive the re-render a save causes (this field is keyed on the list).
   */
  leftover: string[];
  onLeftover: (v: string[]) => void;
}) {
  const listed = COUNTRY_OPTIONS.map((c) => c.value as string);
  const current = value ?? [];
  const others = current.filter((c) => !listed.includes(c));
  const [otherText, setOtherText] = useState([...others, ...leftover].join(', '));
  const commit = (next: string[]) => onChange(next.length ? next : undefined);

  return (
    <div>
      <label className={labelClass}>Countries where the study runs</label>
      {(fromProtocol || (unmapped && unmapped.length > 0)) && (
        <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1">
          {fromProtocol && (
            <p>Filled in from the protocol. Please check these countries and confirm them.</p>
          )}
          {unmapped && unmapped.length > 0 && <p>{unmatchedCountriesText(unmapped, current.length > 0)}</p>}
          {onConfirm && (
            <button
              type="button"
              onClick={onConfirm}
              className="mt-1 inline-flex items-center px-2.5 py-1 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700"
            >
              Confirm countries
            </button>
          )}
        </div>
      )}
      <div className="space-y-2">
        {COUNTRY_OPTIONS.map((c) => {
          const checked = current.includes(c.value);
          return (
            <label key={c.value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={() =>
                  commit(checked ? current.filter((x) => x !== c.value) : [...current, c.value])
                }
                className="accent-primary-600"
              />
              {c.label}
            </label>
          );
        })}
      </div>
      <input
        type="text"
        placeholder="Other countries: two-letter codes, e.g. NZ, JP"
        value={otherText}
        onChange={(e) => setOtherText(e.target.value)}
        onBlur={() => {
          // Save only when the list changed: a save clears the "from the
          // protocol" mark, so an unchanged blur must not confirm it.
          const r = otherCountriesAfterBlur(current, listed, otherText);
          setOtherText(r.text);
          if (r.next) commit(r.next);
          onLeftover(r.unrecognised);
        }}
        className={`${inputClass} mt-2`}
      />
      {leftover.length > 0 && <p className="text-xs text-amber-700 mt-1">{unrecognisedNamesText(leftover)}</p>}
      {unrecognisedCountryCodes(current).length > 0 && (
        <p className="text-xs text-amber-700 mt-1">{unrecognisedCodesText(unrecognisedCountryCodes(current))}</p>
      )}
      <p className="text-[11px] text-gray-400 mt-1">
        Sets the regulator, ethics body, privacy law and record retention the documents state. Leave empty if not yet decided.
      </p>
    </div>
  );
}

type SourceStatus = 'unknown' | 'used' | 'not_used';

export default function StudySetupForm({
  value,
  onChange,
  highlightFields,
  compact,
}: StudySetupFormProps) {
  // Typed country entries the Other countries box could not recognise.
  const [countryLeftover, setCountryLeftover] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState({
    protocol: true,
    operations: true,
    funding: true,
    reporting: true,
    externalSources: true,
    // Study facts used by generated documents (2026-09-30). Collapsed by
    // default so the form stays short; every one is optional.
    countries: false,
    design: false,
    systems: false,
    parties: false,
    safety: false,
    labs: false,
    dictionaries: false,
    timelines: false,
    identifiers: false,
  });

  const toggle = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const update = (fields: Partial<StudySetup>) => {
    onChange({ ...value, ...fields });
  };

  const spacingClass = compact ? 'space-y-3' : 'space-y-4';

  // Nested study-fact settings: each helper merges into its object and drops
  // the object entirely once it holds nothing, so "not answered" stays absent
  // rather than becoming an empty object.
  const prune = <T extends object>(o: T): T | undefined =>
    Object.values(o).some((v) => v !== undefined && v !== '') ? o : undefined;
  const setOversight = (fields: Partial<OversightSetting>) =>
    update({ oversight: prune({ ...(value.oversight ?? {}), ...fields }) });
  const setSafety = (fields: Partial<SafetyReportingSetting>) =>
    update({ safetyReporting: prune({ ...(value.safetyReporting ?? {}), ...fields }) });
  const setTimeline = (fields: Partial<TimelineSettings>) =>
    update({ timelines: prune({ ...(value.timelines ?? {}), ...fields }) });
  const parties = value.parties ?? [];
  const setParties = (next: typeof parties) => update({ parties: next.length ? next : undefined });
  const studyParts = value.studyParts ?? [];
  const setStudyParts = (next: StudyPartSetting[]) => update({ studyParts: next.length ? next : undefined });

  return (
    <div className="space-y-5">
      {/* Protocol Identification */}
      <div>
        <SectionHeader
          title="Protocol Identification"
          expanded={expandedSections.protocol}
          onToggle={() => toggle('protocol')}
        />
        {expandedSections.protocol && (
          <div className={spacingClass}>
            {/* Protocol Number */}
            <div className={highlightWrap('protocolNumber', highlightFields)}>
              <label className={labelClass}>Protocol Number</label>
              <input
                type="text"
                placeholder="e.g., IN-US-380-4543"
                value={value.protocolNumber ?? ''}
                onChange={(e) => update({ protocolNumber: e.target.value })}
                className={inputClass}
              />
            </div>

            {/* Therapeutic Area */}
            <div className={highlightWrap('therapeuticArea', highlightFields)}>
              <label className={labelClass}>Therapeutic Area</label>
              <input
                type="text"
                placeholder="e.g., HIV-1 Infection, Oncology"
                value={value.therapeuticArea ?? ''}
                onChange={(e) => update({ therapeuticArea: e.target.value })}
                className={inputClass}
              />
            </div>

            {/* Primary Objective */}
            <div className={highlightWrap('primaryObjective', highlightFields)}>
              <label className={labelClass}>Primary Objective</label>
              <textarea
                rows={3}
                placeholder="Primary study objective from protocol"
                value={value.primaryObjective ?? ''}
                onChange={(e) => update({ primaryObjective: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>
        )}
      </div>

      {/* Study Operations */}
      <div>
        <SectionHeader
          title="Study Operations"
          expanded={expandedSections.operations}
          onToggle={() => toggle('operations')}
        />
        {expandedSections.operations && (
          <div className={spacingClass}>
            {/* EDC System */}
            <div className={highlightWrap('edcSystem', highlightFields)}>
              <label className={labelClass}>EDC System</label>
              <select
                value={value.edcSystem ?? 'unknown'}
                onChange={(e) => {
                  const v = e.target.value;
                  update({
                    edcSystem: v,
                    ...(v !== 'other' ? { edcSystemOther: undefined } : {}),
                  });
                }}
                className={inputClass}
              >
                {EDC_SYSTEMS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {value.edcSystem === 'other' && (
                <input
                  type="text"
                  placeholder="Specify EDC system"
                  value={value.edcSystemOther ?? ''}
                  onChange={(e) => update({ edcSystemOther: e.target.value })}
                  className={`${inputClass} mt-2`}
                />
              )}
            </div>

            {/* Randomization System */}
            <div className={highlightWrap('randomizationSystem', highlightFields)}>
              <label className={labelClass}>Randomization System</label>
              <input
                type="text"
                placeholder="e.g., IWRS vendor or module"
                value={value.randomizationSystem ?? ''}
                onChange={(e) => update({ randomizationSystem: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>
        )}
      </div>

      {/* Funding & Oversight */}
      <div>
        <SectionHeader
          title="Funding & Oversight"
          expanded={expandedSections.funding}
          onToggle={() => toggle('funding')}
        />
        {expandedSections.funding && (
          <div className={spacingClass}>
            {/* Sponsor */}
            <div className={highlightWrap('sponsor', highlightFields)}>
              <label className={labelClass}>Sponsor</label>
              <input
                type="text"
                placeholder="Sponsoring organization"
                value={value.sponsor ?? ''}
                onChange={(e) => update({ sponsor: e.target.value })}
                className={inputClass}
              />
            </div>

            {/* Sub-Investigator(s) */}
            <div className={highlightWrap('subInvestigators', highlightFields)}>
              <label className={labelClass}>Sub-Investigator(s)</label>
              <input
                type="text"
                placeholder="e.g., Alex Rivera, DO"
                value={value.subInvestigators ?? ''}
                onChange={(e) => update({ subInvestigators: e.target.value })}
                className={inputClass}
              />
            </div>

            {/* Funding Agency */}
            <div className={highlightWrap('fundingAgency', highlightFields)}>
              <label className={labelClass}>Funding Agency</label>
              <select
                value={value.fundingAgency ?? 'unknown'}
                onChange={(e) => {
                  const v = e.target.value;
                  update({
                    fundingAgency: v,
                    ...(v !== 'other' ? { fundingAgencyOther: undefined } : {}),
                  });
                }}
                className={inputClass}
              >
                {FUNDING_AGENCIES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {value.fundingAgency === 'other' && (
                <input
                  type="text"
                  placeholder="Specify funding agency"
                  value={value.fundingAgencyOther ?? ''}
                  onChange={(e) => update({ fundingAgencyOther: e.target.value })}
                  className={`${inputClass} mt-2`}
                />
              )}
            </div>

            {/* DSMB */}
            <div className={highlightWrap('hasDSMB', highlightFields)}>
              <label className={labelClass}>Data Safety Monitoring Board (DSMB)</label>
              <RadioGroup
                name="hasDSMB"
                value={value.hasDSMB}
                onChange={(v) => update({ hasDSMB: v })}
              />
            </div>

            {/* IRB of Record */}
            <div className={highlightWrap('irbOfRecord', highlightFields)}>
              <label className={labelClass}>IRB of Record</label>
              <input
                type="text"
                placeholder="e.g., WCG IRB, local institutional IRB"
                value={value.irbOfRecord ?? ''}
                onChange={(e) => update({ irbOfRecord: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>
        )}
      </div>

      {/* Reporting & Regulatory */}
      <div>
        <SectionHeader
          title="Reporting & Regulatory"
          expanded={expandedSections.reporting}
          onToggle={() => toggle('reporting')}
        />
        {expandedSections.reporting && (
          <div className={spacingClass}>
            {/* Reporting Requirements */}
            <div className={highlightWrap('reportingRequirements', highlightFields)}>
              <label className={labelClass}>Reporting Requirements</label>
              <div className="space-y-2">
                {REPORTING_REQUIREMENTS.map((req) => {
                  const checked = (value.reportingRequirements ?? []).includes(req.value);
                  return (
                    <label
                      key={req.value}
                      className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const current = value.reportingRequirements ?? [];
                          const next = checked
                            ? current.filter((v) => v !== req.value)
                            : [...current, req.value];
                          update({ reportingRequirements: next });
                        }}
                        className="accent-primary-600"
                      />
                      {req.label}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* IND/IDE Number */}
            <div className={highlightWrap('indIdeNumber', highlightFields)}>
              <label className={labelClass}>IND/IDE Number (US studies)</label>
              <input
                type="text"
                placeholder="e.g., IND 123456"
                value={value.indIdeNumber ?? ''}
                onChange={(e) => update({ indIdeNumber: e.target.value })}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">
                For US studies. References for other countries (for example an Australian CTN or CTA, an EU CT number) have no field yet; they are requested in the documents as TBD.
              </p>
            </div>

            {/* 21 CFR Part 11 */}
            <div className={highlightWrap('requiresPart11', highlightFields)}>
              <label className={labelClass}>21 CFR Part 11 Required</label>
              <RadioGroup
                name="requiresPart11"
                value={value.requiresPart11}
                onChange={(v) => update({ requiresPart11: v })}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Study facts used by generated documents (2026-09-30) ── */}

      {/* Countries & Regulation */}
      <div>
        <SectionHeader
          title="Countries & Regulation"
          expanded={expandedSections.countries}
          onToggle={() => toggle('countries')}
          note={
            value.countriesSource === 'protocol' || (value.countriesUnmapped?.length ?? 0) > 0
              ? 'From the protocol, please confirm'
              : undefined
          }
        />
        {expandedSections.countries && (
          <div className={spacingClass}>
            <div className={highlightWrap('countries', highlightFields)}>
              <CountriesField
                key={(value.countries ?? []).join(',')}
                value={value.countries}
                onChange={(v) => update({ countries: v, countriesSource: undefined })}
                fromProtocol={value.countriesSource === 'protocol'}
                unmapped={value.countriesUnmapped}
                onConfirm={() => update({ countriesSource: undefined, countriesUnmapped: undefined })}
                leftover={countryLeftover}
                onLeftover={setCountryLeftover}
              />
            </div>
            <div className={highlightWrap('productType', highlightFields)}>
              <SelectField<ProductTypeSetting>
                label="What the study tests"
                value={value.productType}
                options={PRODUCT_TYPE_OPTIONS}
                onChange={(v) => update({ productType: v })}
                help="Record-retention rules are stated only for medicinal-product trials. For any other choice, documents show retention as TBD."
              />
            </div>
          </div>
        )}
      </div>

      {/* Study Design */}
      <div>
        <SectionHeader
          title="Design & Oversight"
          expanded={expandedSections.design}
          onToggle={() => toggle('design')}
        />
        {expandedSections.design && (
          <div className={spacingClass}>
            <div className={highlightWrap('blinding', highlightFields)}>
              <SelectField<BlindingSetting>
                label="Blinding"
                value={value.blinding}
                options={BLINDING_OPTIONS}
                onChange={(v) => update({ blinding: v })}
              />
            </div>
            <div className={highlightWrap('randomized', highlightFields)}>
              <label className={labelClass}>Randomized</label>
              <RadioGroup
                name="randomized"
                value={value.randomized}
                onChange={(v) => update({ randomized: v })}
              />
            </div>
            <div className={highlightWrap('oversight', highlightFields)}>
              <SelectField<OversightKindSetting>
                label="Oversight body"
                value={value.oversight?.kind}
                options={OVERSIGHT_KIND_OPTIONS}
                onChange={(v) => setOversight({ kind: v === 'unknown' ? undefined : v })}
                help="A safety review committee for dose escalation is not the same as an independent DSMB."
              />
              {value.oversight?.kind && value.oversight.kind !== 'none' && (
                <div className="mt-2 space-y-2">
                  <TextField
                    label="Name, as the protocol gives it"
                    value={value.oversight?.name}
                    onChange={(v) => setOversight({ name: v })}
                  />
                  <div>
                    <label className={labelClass}>Independent of the sponsor?</label>
                    <RadioGroup
                      name="oversightIndependent"
                      value={value.oversight?.independent}
                      onChange={(v) => setOversight({ independent: v })}
                    />
                  </div>
                  <TextField
                    label="Membership"
                    value={value.oversight?.membership}
                    onChange={(v) => setOversight({ membership: v })}
                    placeholder="e.g. two clinicians and a statistician"
                  />
                  <TextField
                    label="How often it meets"
                    value={value.oversight?.cadence}
                    onChange={(v) => setOversight({ cadence: v })}
                    placeholder="e.g. after each dose cohort"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Systems & Hosting */}
      <div>
        <SectionHeader
          title="EDC & Hosting"
          expanded={expandedSections.systems}
          onToggle={() => toggle('systems')}
        />
        {expandedSections.systems && (
          <div className={spacingClass}>
            <p className="text-xs text-gray-500">The EDC system itself is chosen under Study Operations above.</p>
            <TextField
              label="EDC version"
              value={value.edcVersion}
              onChange={(v) => update({ edcVersion: v })}
              placeholder="Leave empty if not yet known"
            />
            <TextField
              label="Hosting provider"
              value={value.hostingProvider}
              onChange={(v) => update({ hostingProvider: v })}
              placeholder="Leave empty if not yet known"
            />
            <TextField
              label="Hosting region"
              value={value.hostingRegion}
              onChange={(v) => update({ hostingRegion: v })}
              placeholder="Where the data is stored"
            />
          </div>
        )}
      </div>

      {/* Parties */}
      <div>
        <SectionHeader
          title="Parties & Responsibilities"
          expanded={expandedSections.parties}
          onToggle={() => toggle('parties')}
        />
        {expandedSections.parties && (
          <div className={spacingClass}>
            <p className="text-xs text-gray-500">
              Who does what. Documents name only the organisations listed here.
            </p>
            {parties.map((p, i) => (
              <div key={i} className="flex gap-2 items-start">
                <select
                  value={p.role}
                  onChange={(e) =>
                    setParties(parties.map((x, j) => (j === i ? { ...x, role: e.target.value as PartyRole } : x)))
                  }
                  className={`${inputClass} max-w-[11rem]`}
                  aria-label="Role"
                >
                  {PARTY_ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Organisation name"
                  value={p.name}
                  onChange={(e) => setParties(parties.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  className={inputClass}
                  aria-label="Organisation name"
                />
                <button
                  type="button"
                  onClick={() => setParties(parties.filter((_, j) => j !== i))}
                  className="text-xs text-gray-500 hover:text-red-600 px-2 py-2"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setParties([...parties, { role: 'cro', name: '' }])}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              + Add party
            </button>
            {parties.some((p) => !p.name.trim()) && (
              <p className="text-[11px] text-amber-600">Each party needs a name before the settings can be saved.</p>
            )}
          </div>
        )}
      </div>

      {/* Safety Reporting */}
      <div>
        <SectionHeader
          title="Safety Reporting"
          expanded={expandedSections.safety}
          onToggle={() => toggle('safety')}
        />
        {expandedSections.safety && (
          <div className={spacingClass}>
            <SelectField<SaeRouteSetting>
              label="How serious adverse events reach the safety group"
              value={value.safetyReporting?.route}
              options={SAE_ROUTE_OPTIONS}
              onChange={(v) => setSafety({ route: v === 'unknown' ? undefined : v })}
            />
            <TextField
              label="Description (optional)"
              value={value.safetyReporting?.description}
              onChange={(v) => setSafety({ description: v })}
              placeholder="The route in the study's own words"
            />
            <div>
              <label className={labelClass}>Initial report due within (hours of awareness)</label>
              <input
                type="number"
                min={1}
                placeholder="Not yet decided"
                value={value.safetyReporting?.initialReportHours ?? ''}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setSafety({ initialReportHours: e.target.value === '' || !(n > 0) ? undefined : n });
                }}
                className={`${inputClass} max-w-[8rem]`}
              />
            </div>
            <TextField
              label="Safety database"
              value={value.safetyReporting?.safetyDatabase}
              onChange={(v) => setSafety({ safetyDatabase: v })}
            />
            <TextField
              label="Safety vendor"
              value={value.safetyReporting?.safetyVendor}
              onChange={(v) => setSafety({ safetyVendor: v })}
            />
            <TextField
              label="How often SAEs are reconciled"
              value={value.safetyReporting?.reconciliationCadence}
              onChange={(v) => setSafety({ reconciliationCadence: v })}
              placeholder="e.g. monthly, and before database lock"
            />
          </div>
        )}
      </div>

      {/* Labs & Imaging */}
      <div>
        <SectionHeader
          title="Laboratories & Imaging"
          expanded={expandedSections.labs}
          onToggle={() => toggle('labs')}
        />
        {expandedSections.labs && (
          <div className={spacingClass}>
            <SelectField
              label="Laboratories"
              value={value.labModel}
              options={LAB_MODEL_OPTIONS}
              onChange={(v) => update({ labModel: v })}
            />
            <SelectField
              label="Imaging"
              value={value.imagingModel}
              options={IMAGING_MODEL_OPTIONS}
              onChange={(v) => update({ imagingModel: v })}
            />
          </div>
        )}
      </div>

      {/* Dictionaries */}
      <div>
        <SectionHeader
          title="Coding Dictionaries & Standards"
          expanded={expandedSections.dictionaries}
          onToggle={() => toggle('dictionaries')}
        />
        {expandedSections.dictionaries && (
          <div className={spacingClass}>
            <p className="text-xs text-gray-500">
              Documents state a version only when it is entered here; otherwise they say TBD.
            </p>
            {DICTIONARY_FIELDS.map((d) => (
              <TextField
                key={d.key}
                label={d.label}
                value={value.dictionaryVersions?.[d.key]}
                onChange={(v) =>
                  update({ dictionaryVersions: prune({ ...(value.dictionaryVersions ?? {}), [d.key]: v }) })
                }
                placeholder={d.placeholder}
              />
            ))}
          </div>
        )}
      </div>

      {/* Timelines */}
      <div>
        <SectionHeader
          title="Timelines"
          expanded={expandedSections.timelines}
          onToggle={() => toggle('timelines')}
        />
        {expandedSections.timelines && (
          <div className={spacingClass}>
            <DurationField
              label="Data entry after a visit"
              value={value.timelines?.dataEntry}
              onChange={(v) => setTimeline({ dataEntry: v })}
            />
            <DurationField
              label="Sites answer a query within"
              value={value.timelines?.queryResponse}
              onChange={(v) => setTimeline({ queryResponse: v })}
            />
            <DurationField
              label="Query answers close to database lock"
              value={value.timelines?.queryResponseNearLock}
              onChange={(v) => setTimeline({ queryResponseNearLock: v })}
            />
            <TextField
              label="SAE reconciliation"
              value={value.timelines?.saeReconciliation}
              onChange={(v) => setTimeline({ saeReconciliation: v })}
              placeholder="e.g. monthly"
            />
          </div>
        )}
      </div>

      {/* Identifiers & Scope */}
      <div>
        <SectionHeader
          title="Identifiers & Study Parts"
          expanded={expandedSections.identifiers}
          onToggle={() => toggle('identifiers')}
        />
        {expandedSections.identifiers && (
          <div className={spacingClass}>
            <TextField
              label="Subject ID format"
              value={value.subjectIdFormat}
              onChange={(v) => update({ subjectIdFormat: v })}
              placeholder="e.g. site number, hyphen, three-digit subject number"
            />
            <SelectField
              label="Date format on case report forms"
              value={value.crfDateFormat ?? ''}
              options={DATE_FORMAT_OPTIONS}
              onChange={(v) => update({ crfDateFormat: v === '' ? undefined : (v as StudyDateFormat) })}
              help="Printed beside every date on the case report forms and stated in the CRF Completion Guide. DD-MMM-YYYY (05-JAN-2026) reads the same in every country. Forms you upload keep the format printed on them. A change shows on the case report forms after you press Generate All CRFs (it reads Re-render All CRFs once forms are rendered) in the CRF Builder; stored renderings, the archive and the visit packet use the last rendering until then."
            />
            <TextField
              label="CDISC STUDYID"
              value={value.cdiscStudyId}
              onChange={(v) => update({ cdiscStudyId: v })}
              help="Used in SDTM exports. It is never copied from the protocol number automatically."
            />
            <div>
              <label className={labelClass}>Study parts</label>
              <p className="text-[11px] text-gray-400 mb-2">
                For studies in parts or cohorts. Untick a part to leave its forms out of this build.
              </p>
              {studyParts.map((p, i) => (
                <div key={i} className="flex gap-2 items-center mb-2">
                  <input
                    type="text"
                    placeholder="Part (e.g. A)"
                    value={p.id}
                    onChange={(e) =>
                      setStudyParts(studyParts.map((x, j) => (j === i ? { ...x, id: e.target.value } : x)))
                    }
                    className={`${inputClass} max-w-[6rem]`}
                    aria-label="Part"
                  />
                  <input
                    type="text"
                    placeholder="Name (optional)"
                    value={p.name ?? ''}
                    onChange={(e) =>
                      setStudyParts(
                        studyParts.map((x, j) => (j === i ? { ...x, name: e.target.value || undefined } : x)),
                      )
                    }
                    className={inputClass}
                    aria-label="Part name"
                  />
                  <label className="flex items-center gap-1 text-xs text-gray-700 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={p.inBuild}
                      onChange={() =>
                        setStudyParts(studyParts.map((x, j) => (j === i ? { ...x, inBuild: !x.inBuild } : x)))
                      }
                      className="accent-primary-600"
                    />
                    In this build
                  </label>
                  <button
                    type="button"
                    onClick={() => setStudyParts(studyParts.filter((_, j) => j !== i))}
                    className="text-xs text-gray-500 hover:text-red-600 px-1"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setStudyParts([...studyParts, { id: '', inBuild: true }])}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                + Add part
              </button>
            </div>
          </div>
        )}
      </div>

      {/* External Data Sources */}
      <div>
        <SectionHeader
          title="External Data Sources"
          expanded={expandedSections.externalSources}
          onToggle={() => toggle('externalSources')}
        />
        {expandedSections.externalSources && (
          <div className={spacingClass}>
            <p className="text-xs text-gray-500 mb-3">
              For each source, say whether the study uses it. "Not used" is final: documents
              will leave that source out even if the protocol mentions it. Leave a source as
              "Not yet decided" and documents will mark it TBD.
            </p>
            <div className="space-y-3">
              {EXTERNAL_DATA_SOURCE_OPTIONS.map((opt) => {
                const sources = value.externalDataSources ?? [];
                const exclusions = value.externalDataSourceExclusions ?? [];
                const existing = sources.find((s) => s.type === opt.value);
                const status: SourceStatus = exclusions.includes(opt.value)
                  ? 'not_used'
                  : existing
                    ? 'used'
                    : 'unknown';
                const setStatus = (next: SourceStatus) => {
                  const otherSources = sources.filter((s) => s.type !== opt.value);
                  const otherExclusions = exclusions.filter((t) => t !== opt.value);
                  const nextExclusions =
                    next === 'not_used'
                      ? [...otherExclusions, opt.value as ExternalDataSourceType]
                      : otherExclusions;
                  update({
                    externalDataSources:
                      next === 'used'
                        ? [...otherSources, existing ?? { type: opt.value as ExternalDataSourceType }]
                        : otherSources,
                    externalDataSourceExclusions: nextExclusions.length ? nextExclusions : undefined,
                  });
                };
                return (
                  <div key={opt.value}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-700">{opt.label}</span>
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value as SourceStatus)}
                        className={`${inputClass} max-w-[11rem]`}
                        aria-label={`${opt.label}: used in this study?`}
                      >
                        <option value="unknown">{NOT_DECIDED_LABEL}</option>
                        <option value="used">Used</option>
                        <option value="not_used">Not used</option>
                      </select>
                    </div>
                    {status === 'used' && (
                      <div className="ml-4 mt-1">
                        <input
                          type="text"
                          placeholder="Vendor name (optional)"
                          value={existing?.vendorName ?? ''}
                          onChange={(e) => {
                            const next = sources.map((s) =>
                              s.type === opt.value
                                ? { ...s, vendorName: e.target.value || undefined }
                                : s
                            );
                            update({ externalDataSources: next });
                          }}
                          className={`${inputClass} max-w-xs`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className={highlightWrap('ctmsInterface', highlightFields)}>
              <label className={labelClass}>Does the EDC exchange data with a CTMS?</label>
              <RadioGroup
                name="ctmsInterface"
                value={value.ctmsInterface}
                onChange={(v) => update({ ctmsInterface: v })}
              />
              {value.ctmsInterface === 'yes' && (
                <input
                  type="text"
                  placeholder="CTMS name (optional)"
                  value={value.ctmsName ?? ''}
                  onChange={(e) => update({ ctmsName: e.target.value || undefined })}
                  className={`${inputClass} mt-2`}
                />
              )}
            </div>

            <div className={highlightWrap('protocolDeviationEcrf', highlightFields)}>
              <label className={labelClass}>Are protocol deviations recorded on an eCRF in the EDC?</label>
              <RadioGroup
                name="protocolDeviationEcrf"
                value={value.protocolDeviationEcrf}
                onChange={(v) => update({ protocolDeviationEcrf: v })}
              />
              <p className="mt-1 text-xs text-gray-500">
                Choose No if deviations are kept in a separate log. The documents then do not describe a deviation form.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
